import React, { useState, useEffect } from "react";
import axios from "axios";
import { useParams, useNavigate } from "react-router-dom";
import Layout from "../components/Layout";
import toast from "react-hot-toast";

// Custom hook to get user data from localStorage
const useAuth = () => {
  const [user, setUser] = useState(null);
  
  useEffect(() => {
    const storedUser = localStorage.getItem('user');
    if (storedUser) {
      try {
        const parsedUser = JSON.parse(storedUser);
        setUser(parsedUser);
      } catch (error) {
        console.error('Error parsing user data from localStorage:', error);
        toast.error('Error loading user data');
      }
    }
  }, []);

  return { user };
};

// CSS Styles Component
const EditUserStyles = () => (
  <style>{`
    .layout-container { font-family: 'Inter', sans-serif; }
    .add-user-container { background-color: #f9fafb; padding: 2rem; display: flex; justify-content: center; }
    .form-wrapper { background-color: white; padding: 2.5rem; border-radius: 0.75rem; box-shadow: 0 10px 15px -3px rgba(0,0,0,0.1); width: 100%; max-width: 800px; }
    .form-title { font-size: 2rem; font-weight: 700; color: #1f2937; margin-bottom: 0.5rem; }
    .form-subtitle { color: #6b7280; margin-bottom: 2rem; }
    .form-grid { display: grid; grid-template-columns: repeat(1, 1fr); gap: 1.5rem; margin-bottom: 1.5rem; }
    @media (min-width: 768px) { .form-grid { grid-template-columns: repeat(2, 1fr); } }
    .form-group { display: flex; flex-direction: column; }
    .form-group.full-width { grid-column: 1 / -1; }
    .form-group label { margin-bottom: 0.5rem; font-weight: 500; color: #374151; }
    .form-group input, .form-group select { padding: 0.75rem; border: 1px solid #d1d5db; border-radius: 0.5rem; transition: border-color 0.2s, box-shadow 0.2s; }
    .form-group input:focus, .form-group select:focus { outline: none; border-color: #3b82f6; box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.2); }
    .permissions-grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 1rem; padding: 1rem; border: 1px solid #e5e7eb; border-radius: 0.5rem; }
    @media (min-width: 768px) { .permissions-grid { grid-template-columns: repeat(3, 1fr); } }
    .permission-item { display: flex; align-items: center; }
    .permission-item input[type="checkbox"] { width: 1rem; height: 1rem; margin-right: 0.5rem; }
    .permission-item label { font-weight: normal; margin-bottom: 0; }
    .submit-btn { width: 100%; padding: 1rem; background-color: #16a34a; color: white; font-size: 1rem; font-weight: 600; border: none; border-radius: 0.5rem; cursor: pointer; transition: background-color 0.2s; }
    .submit-btn:disabled { background-color: #9ca3af; cursor: not-allowed; }
    .error { border-color: #ef4444 !important; }
    .error-text { color: #ef4444; font-size: 0.875rem; margin-top: 0.25rem; }
    .error-message { background-color: #fee2e2; border: 1px solid #ef4444; color: #991b1b; padding: 0.75rem; border-radius: 0.5rem; margin-bottom: 1rem; }
  `}</style>
);

const EditUser = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const BASE_URL = process.env.REACT_APP_API_URL || "http://localhost:3000";

  const [formData, setFormData] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [validationErrors, setValidationErrors] = useState({});

  const allPermissions = [
    "view_cases",
    "add_cases",
    "edit_cases",
    "delete_cases",
    "manage_users",
  ];

  useEffect(() => {
    const fetchUserToEdit = async () => {
      if (!user?.token) {
        setError("Authentication required.");
        return;
      }
      try {
        const response = await axios.get(`${BASE_URL}/api/users/${id}`, {
          headers: { Authorization: `Bearer ${user.token}` },
        });
        if (response.data?.status === "success" && response.data.data.user) {
          const userData = response.data.data.user;
          setFormData({ ...userData, password: "" });
        } else {
          throw new Error("User not found.");
        }
      } catch (err) {
        setError(
          err.response?.data?.message ||
            err.message ||
            "Failed to fetch user data."
        );
      }
    };
    if (user) {
      fetchUserToEdit();
    }
  }, [id, user, BASE_URL]);

  const validateForm = () => {
    const errors = {};
    if (!formData.name?.trim()) {
      errors.name = "Name is required";
    }
    if (!formData.email?.trim()) {
      errors.email = "Email is required";
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      errors.email = "Invalid email format";
    }
    if (!formData.phone?.trim()) {
      errors.phone = "Phone number is required";
    } else if (!/^[0-9]{10,15}$/.test(formData.phone.replace(/\D/g, ""))) {
      errors.phone = "Invalid phone number format";
    }
    if (formData.password && formData.password.length < 6) {
      errors.password = "Password must be at least 6 characters";
    }
    return errors;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (validationErrors[name]) {
      setValidationErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const handlePermissionChange = (e) => {
    const { value, checked } = e.target;
    setFormData((prev) => {
      const currentPermissions = prev.permissions || [];
      if (checked) {
        return { ...prev, permissions: [...currentPermissions, value] };
      } else {
        return {
          ...prev,
          permissions: currentPermissions.filter((p) => p !== value),
        };
      }
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!user?.token) {
      setError("Authentication token is missing.");
      toast.error("Authentication token is missing.");
      return;
    }

    const errors = validateForm();
    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      return;
    }

    setIsSubmitting(true);
    setError("");

    const { password, ...updatePayload } = formData;

    if (password) {
      updatePayload.password = password;
    }

    try {
      const response = await axios.put(
        `${BASE_URL}/api/users/${id}`,
        updatePayload,
        {
          headers: { Authorization: `Bearer ${user.token}` },
        }
      );
      if (response.data?.status === "success") {
        toast.success("User updated successfully!");
        navigate("/users");
      } else {
        throw new Error(response.data?.message || "An unknown error occurred.");
      }
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message || "Failed to update user.";
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!formData) {
    return (
      <Layout>
        <div style={{ textAlign: "center", padding: "2rem" }}>
          Loading user data...
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <EditUserStyles />
      <div className="add-user-container">
        <div className="form-wrapper">
          <h1 className="form-title">Edit User</h1>
          <p className="form-subtitle">
            Update the profile for <strong>{formData.name}</strong>.
          </p>

          {error && (
            <div className="error-message">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit}>
            <div className="form-grid">
              <div className="form-group">
                <label htmlFor="name">Full Name</label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className={validationErrors.name ? "error" : ""}
                />
                {validationErrors.name && (
                  <span className="error-text">{validationErrors.name}</span>
                )}
              </div>
              <div className="form-group">
                <label htmlFor="email">Email Address</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className={validationErrors.email ? "error" : ""}
                />
                {validationErrors.email && (
                  <span className="error-text">{validationErrors.email}</span>
                )}
              </div>
              <div className="form-group">
                <label htmlFor="phone">Phone Number</label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  required
                  className={validationErrors.phone ? "error" : ""}
                />
                {validationErrors.phone && (
                  <span className="error-text">{validationErrors.phone}</span>
                )}
              </div>
              <div className="form-group">
                <label htmlFor="password">New Password</label>
                <input
                  type="password"
                  id="password"
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  placeholder="Leave blank to keep current"
                  className={validationErrors.password ? "error" : ""}
                />
                {validationErrors.password && (
                  <span className="error-text">{validationErrors.password}</span>
                )}
              </div>
            </div>

            <div className="form-group full-width">
              <label>Permissions</label>
              <div className="permissions-grid">
                {allPermissions.map((option) => (
                  <div key={option} className="permission-item">
                    <input
                      type="checkbox"
                      id={option}
                      value={option}
                      checked={formData.permissions.includes(option)}
                      onChange={handlePermissionChange}
                    />
                    <label htmlFor={option}>{option.replace(/_/g, " ")}</label>
                  </div>
                ))}
              </div>
            </div>

            <div className="form-group full-width">
              <button
                type="submit"
                className="submit-btn"
                disabled={isSubmitting}
              >
                {isSubmitting ? "Updating User..." : "Update User"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </Layout>
  );
};

export default EditUser;
